import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { API_URL, BACKEND_URL } from '../services/api';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet';
import { 
  Rocket, Target, BarChart3, Users, Calendar, FileText, 
  CheckCircle, Shield, Lock, CreditCard, Zap, BookOpen,
  TrendingUp, Briefcase, Mail
} from 'lucide-react';

const LearnPaymentPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [loading, setLoading] = useState(false);
  const [paypalLoaded, setPaypalLoaded] = useState(false);
  
  const price = 27.0; // Fixed $27 for learners

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
    };
  }, []);

  // Load PayPal script
  useEffect(() => {
    if (paymentMethod === 'paypal' && !paypalLoaded) {
      const script = document.createElement('script');
      script.src = `https://www.paypal.com/sdk/js?client-id=${process.env.REACT_APP_PAYPAL_CLIENT_ID || 'ASJAtEaNQWfYNIIs2jAvVo2PUr8iaOMWEz2ei3anUzBXtPHWdvn-LZ3opHB0yusiVPEOtxLlppR_sRXw'}&currency=USD`;
      script.async = true;
      script.onload = () => setPaypalLoaded(true);
      document.body.appendChild(script);
      return () => {
        if (document.body.contains(script)) document.body.removeChild(script);
      };
    }
  }, [paymentMethod, paypalLoaded]);

  // Render PayPal buttons
  useEffect(() => {
    if (paypalLoaded && paymentMethod === 'paypal' && window.paypal) {
      const container = document.getElementById('paypal-button-container');
      if (container) {
        container.innerHTML = '';
        window.paypal.Buttons({
          createOrder: (data, actions) => {
            return actions.order.create({
              purchase_units: [{
                amount: { value: price.toFixed(2) },
                description: '21-Day GTM Bootcamp - Learner Access'
              }]
            });
          },
          onApprove: async (data, actions) => {
            const order = await actions.order.capture();
            await handlePaymentSuccess('paypal', order.id, order);
          },
          onError: (err) => {
            console.error('PayPal error:', err);
            toast.error('PayPal payment failed. Please try again.');
          }
        }).render('#paypal-button-container');
      }
    }
  }, [paypalLoaded, paymentMethod, email, fullName]);

  const handlePaymentSuccess = async (method, paymentId, paymentData) => {
    try {
      const response = await api.post(`${API_URL}/payments/learner/verify`, {
        payment_method: method,
        payment_id: paymentId,
        payment_data: paymentData,
        email: email,
        name: fullName,
        amount: price,
        source: 'learn_page'
      });

      if (response.data.success) {
        toast.success('Payment successful! Welcome aboard!');
        navigate('/learn/thank-you', { 
          state: { 
            email, 
            name: fullName,
            tempPassword: response.data.temp_password 
          } 
        });
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error(error.response?.data?.detail || 'Payment verification failed');
    }
  };

  const handleRazorpayPayment = async () => {
    if (!email || !fullName) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const orderResponse = await api.post(`${API_URL}/payments/learner/create-order`, {
        amount: price,
        email: email,
        name: fullName,
        source: 'learn_page'
      });

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_live_2TFKdoG5xpkBjC',
        amount: orderResponse.data.amount,
        currency: 'USD',
        name: 'SalesQualifyd',
        description: '21-Day GTM Bootcamp - Learner Access',
        order_id: orderResponse.data.order_id,
        handler: async (response) => {
          await handlePaymentSuccess('razorpay', response.razorpay_payment_id, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_signature: response.razorpay_signature
          });
        },
        prefill: {
          name: fullName,
          email: email
        },
        theme: { color: '#f97316' }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initiate payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: BookOpen, title: '21-Day GTM Bootcamp', desc: 'Complete video curriculum from planning to launch' },
    { icon: Target, title: 'GTM Strategy Builder', desc: 'AI-powered tool to create your go-to-market plan' },
    { icon: BarChart3, title: 'Market Research Suite', desc: 'TAM/SAM/SOM analysis & competitor intelligence' },
    { icon: FileText, title: 'Templates & Toolkits', desc: 'Pitch decks, calendars, SOPs, and frameworks' },
    { icon: Users, title: 'Founder Community', desc: 'Network with fellow entrepreneurs' },
    { icon: Zap, title: 'Lifetime Platform Access', desc: 'Use all SalesQualifyd tools forever' }
  ];

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <title>Enroll in 21-Day GTM Bootcamp | SalesQualifyd</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-800 to-red-600 py-3 text-center">
          <p className="text-sm font-medium">🎓 Exclusive Learner Access — Limited Time Offer</p>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            
            {/* Left - What You Get */}
            <div>
              <div className="mb-8">
                <span className="inline-block bg-red-700/20 text-amber-500 px-4 py-1 rounded-full text-sm font-semibold mb-4">
                  21-Day GTM Bootcamp
                </span>
                <h1 className="text-4xl md:text-5xl font-bold mb-4">
                  Launch Your Startup<br />
                  <span className="text-red-700">The Right Way</span>
                </h1>
                <p className="text-gray-400 text-lg">
                  Master go-to-market strategy with our intensive bootcamp + get lifetime access to our complete founder toolkit.
                </p>
              </div>

              {/* Benefits Grid */}
              <div className="space-y-4">
                <h3 className="text-xl font-semibold text-white mb-4">What&apos;s Included:</h3>
                {benefits.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4 bg-white/5 rounded-xl p-4 border border-white/10 hover:border-red-700/50 transition-all">
                    <div className="bg-red-700/20 p-3 rounded-lg">
                      <item.icon className="h-6 w-6 text-red-700" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">{item.title}</h4>
                      <p className="text-gray-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Instructor Badge */}
              <div className="mt-8 flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10">
                <img 
                  src="/assets/learn-hero.jpg" 
                  alt="Balaji" 
                  className="w-16 h-16 rounded-full border-2 border-red-700"
                />
                <div>
                  <p className="font-semibold">Balaji Vijayaraghavan</p>
                  <p className="text-gray-400 text-sm">13+ years • 200+ companies • 49 launches</p>
                </div>
              </div>
            </div>

            {/* Right - Payment Form */}
            <div className="lg:sticky lg:top-8">
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-2xl">
                {/* Price */}
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <span className="text-5xl font-bold text-white">${price}</span>
                    <div className="text-left">
                      <span className="text-gray-400 line-through text-lg">$97</span>
                      <span className="block text-green-400 text-sm font-semibold">Save $70</span>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">One-time payment • Lifetime access</p>
                </div>

                {/* Form */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="John Doe"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-red-700 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-500 focus:ring-2 focus:ring-red-700 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Payment Method Tabs */}
                <div className="flex gap-2 mb-6">
                  <button
                    onClick={() => setPaymentMethod('razorpay')}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                      paymentMethod === 'razorpay'
                        ? 'bg-red-700 text-white'
                        : 'bg-white/10 text-gray-400 hover:bg-white/20'
                    }`}
                  >
                    <CreditCard className="inline h-4 w-4 mr-2" />
                    Card / UPI
                  </button>
                  <button
                    onClick={() => setPaymentMethod('paypal')}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                      paymentMethod === 'paypal'
                        ? 'bg-red-700 text-white'
                        : 'bg-white/10 text-gray-400 hover:bg-white/20'
                    }`}
                  >
                    PayPal
                  </button>
                </div>

                {/* Payment Buttons */}
                {paymentMethod === 'razorpay' ? (
                  <button
                    onClick={handleRazorpayPayment}
                    disabled={loading || !email || !fullName}
                    className="w-full py-4 bg-gradient-to-r from-red-500 to-red-500 hover:from-red-800 hover:to-red-600 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-red-700/30"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <Lock className="h-5 w-5" />
                        Pay ${price} — Get Instant Access
                      </span>
                    )}
                  </button>
                ) : (
                  <div id="paypal-button-container" className="min-h-[50px]">
                    {!paypalLoaded && (
                      <div className="flex items-center justify-center py-4">
                        <div className="w-6 h-6 border-2 border-red-700/30 border-t-red-700 rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                )}

                {/* Trust Badges */}
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="flex items-center justify-center gap-6 text-gray-400 text-sm">
                    <span className="flex items-center gap-1">
                      <Shield className="h-4 w-4 text-green-500" />
                      Secure
                    </span>
                    <span className="flex items-center gap-1">
                      <Lock className="h-4 w-4 text-green-500" />
                      Encrypted
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Instant Access
                    </span>
                  </div>
                </div>
              </div>

              {/* Guarantee */}
              <div className="mt-6 text-center">
                <p className="text-gray-500 text-sm">
                  🔒 Your payment is secure and encrypted.<br />
                  Questions? Email us at <a href="mailto:support@salesqualifyd.com" className="text-red-700 hover:underline">support@salesqualifyd.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LearnPaymentPage;
